import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import LinearProgress from "@mui/material/LinearProgress";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKAlert from "components/MKAlert";
import MKButton from "components/MKButton";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";

import { DatePickerCustoms } from "components/DatePickerCustoms";

// Routes
import routes from "routes";

// api
import { exportRegister } from "api/register";

import dayjs from "dayjs";

function ExportRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const props = location.state;
  useEffect(() => {
    if (props === null) {
      navigate("/ndrs-gsb-register/sign-in");
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isAlert, setIsAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertType, setAlertType] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleExport = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (startDate && endDate) {
      const payload = {
        startDate: startDate,
        endDate: endDate,
      };
      try {
        const response = await exportRegister(payload);
        const blob = new Blob([response], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link element
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "customer_regiser-" + dayjs().format("YYYYMMDDHHmmss") + ".csv"
        ); // File name
        document.body.appendChild(link);
        link.click();

        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setIsAlert(true);
        setAlertMsg("ไม่สามารถ export ข้อมูลได้");
        setAlertType("error");
        console.error("Error downloading CSV:", error);
      }
    } else {
      setIsLoading(false);
      setIsAlert(true);
      setAlertMsg("ข้อมูลวันที่ (เริ่มต้น) หรือ วันที่ (สิ้นสุด) ไม่ถูกต้อง");
      setAlertType("warning");
      console.error("StartDate is invalid : " + startDate);
      console.error("EndDate is invalid : " + endDate);
    }
  };

  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "external",
          route: "https://ln15.gsb.or.th/ndrs",
          label: "GSB NDRS Website",
          color: "default",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="45vh"
        width="100%"
        bgColor="#1e88e5"
        sx={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography
              variant="h2"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              การลงทะเบียนเข้าร่วมมาตรการผ่อนปรนเงื่อนไขการชำระหนี้
            </MKTypography>
            <MKTypography variant="h5" color="white" opacity={1} mt={1} mb={3}>
              มาตรการให้ความช่วยเหลือลูกหนี้ที่มีสถานะเป็นหนี้เสียที่มีภาระหนี้เงินต้นและดอกเบี้ยไม่เกิน
              1 แสนบาท
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={{ xs: 6, sm: 12 }}>
          <Container>
            {isAlert ? (
              <>
                <MKAlert color={alertType} dismissible>
                  {alertMsg}
                </MKAlert>
                <br />
              </>
            ) : null}
            <Grid container item xs={12} justifyContent="center">
              <Grid container justifyContent="center" py={2} spacing={1}>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <MKTypography variant="body2" fontWeight="regular" color="dark">
                    วันที่ (เริ่มต้น)
                  </MKTypography>
                  <DatePickerCustoms
                    value={startDate}
                    onChange={(christDate, buddhistDate) => {
                      setStartDate(buddhistDate);
                    }}
                    placeholder={"ระบุ วัน/เดือน/ปี (พ.ศ.)"}
                    dateFormat={"yyyy-MM-dd"}
                    displayFormat={"DD MMMM YYYY"}
                    clearable={true}
                    disabled={false}
                    readOnly={false}
                    yearBoundary={99}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <MKTypography variant="body2" fontWeight="regular" color="dark">
                    วันที่ (สิ้นสุด)
                  </MKTypography>
                  <DatePickerCustoms
                    value={endDate}
                    onChange={(christDate, buddhistDate) => {
                      setEndDate(buddhistDate);
                    }}
                    placeholder={"ระบุ วัน/เดือน/ปี (พ.ศ.)"}
                    dateFormat={"yyyy-MM-dd"}
                    displayFormat={"DD MMMM YYYY"}
                    clearable={true}
                    disabled={false}
                    readOnly={false}
                    yearBoundary={99}
                    autoComplete="off"
                  />
                </Grid>
              </Grid>
              <Grid container justifyContent="center" py={2}>
                <MKButton variant="contained" color="primary" onClick={handleExport}>
                  Export
                </MKButton>
                &nbsp;
                <MKButton
                  variant="contained"
                  color="dark"
                  onClick={() => navigate("/ndrs-gsb-register/sign-in")}
                >
                  Logout
                </MKButton>
              </Grid>
              {isLoading ? (
                <Grid container justifyContent="center">
                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <Stack sx={{ width: "50%" }}>
                      <LinearProgress color="primary" />
                    </Stack>
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
          </Container>
        </MKBox>
      </Card>
    </>
  );
}
ExportRegister.defaultProps = {
  user: {},
};
ExportRegister.propTypes = {
  user: PropTypes.shape({
    title: PropTypes.string,
    postalCode: PropTypes.string,
    displayName: PropTypes.string,
    otherPager: PropTypes.string,
    department: PropTypes.string,
    streetAddress: PropTypes.string,
    employeeType: PropTypes.string,
    employeeID: PropTypes.string,
    division: PropTypes.string,
    token: PropTypes.string,
    accessToken: PropTypes.string,
    isLogin: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isCallCenter: PropTypes.bool,
    isIt: PropTypes.bool,
  }),
};

export default ExportRegister;
